import React, { useState, useEffect } from "react";
import "./ManageApps.scss";
import { Container } from "../../../../../components/Grid/Grid";
import { Typography } from "../../../../../components/__common/Typography/Typography";
import { Image } from "../../../../../components/__common/Image";
import {
  Button,
  TTSButton,
} from "../../../../../components/__common/_controls";
import Tabs from "../../../../../components/__common/_controls/Tab/Tab";
import { ConfirmationModal } from "../../../../../components/ConfirmationModal/ConfirmationModal";
import { useServices } from "../../../../../hooks/services-hooks";
import { useDispatch, useSelector } from "react-redux";
import { ServicesState } from "../../../../../store/reducers/services-reducer";
import { ApplicationState } from "../../../../../store";
import { REQUEST_TYPE } from "../../../../../types/user";
import { ServiceEntity, ServiceStatus } from "../../../../../types/services";
import { UtilsDateTime } from "../../../../../utils/UtilsDateTIme";
import Loading from "../../../../../assets/images/gif/sdap-loading.json";
import { UtilsRequest } from "../../../../../utils/UtilsRequest";
import { MyDataState } from "../../../../../store/reducers/mydata-reducer";
import { ROUTES } from "../../../../../configs/routes";
import Modal from "../../../../../components/__common/Modal/Modal";
import { Trans, useTranslation } from "react-i18next";
import { LayoutState } from "../../../../../store/reducers/layout-reducer";
import { UserState } from "../../../../../store/reducers/user-reducer";
import { UtilsKeyboardAccessibility } from "../../../../../utils/UtilsKeyboardAccessibility";
import FocusTrapping from "../../../../../components/__hoc/FocusTrapping";
import Lottie from "lottie-react";
import { actionSetServices } from "../../../../../store/actions/services-actions";
import { useHistory } from "react-router-dom";
import { ServicesListSection } from "./ServicesListSection";
import { ServiceErrorDisplay } from "./ServiceErrorDisplay";

export const ManageApps: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { loadServices, selectedServices, setSelectedServices } = useServices();
  const appServiceState = useSelector<ApplicationState, ServicesState>(
    (state) => state.services
  );
  const appMyDataState = useSelector<ApplicationState, MyDataState>(
    (state) => state.myData
  );
  const appLayoutState = useSelector<ApplicationState, LayoutState>(
    (state) => state.layout
  );
  const appUserState = useSelector<ApplicationState, UserState>(
    (state) => state.user
  );

  const history = useHistory();
  const { services, isServicesLoading, servicesHasErrors } = appServiceState;
  const { requestNumber, isRequestPending } = appMyDataState;
  const [servicesCount, setServicesCount] = useState<number>(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedServicesObject, setSelectedServicesObject] = useState<
    ServiceEntity[]
  >([]);
  const [selectedService, setSelectedService] = useState<ServiceEntity>();
  const [selectedServiceJD, setSelectedServiceJD] = useState<string>("");
  const [requestType, setRequestType] = useState<REQUEST_TYPE>(
    REQUEST_TYPE.DOWNLOAD
  );
  const [isServiceInfoOpen, setIsServiceInfoOpen] = useState<boolean>(false);
  const cachedRequest = UtilsRequest.getRequestInfo();

  const handlePageShow = (e: PageTransitionEvent) => {
    if (e.persisted) {
      setConfirmOpen(false);
      setSelectedServices([]);
      setSelectedServicesObject([]);
    }
  };
  useEffect(() => {
    window.addEventListener("pageshow", handlePageShow);

    return () => {
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, []);

  useEffect(
    () => {
      let isMounted = true;
      if (!appUserState.userData.countryCode) return () => {};
      const controller = new AbortController();
      (async () => {
        const result = await loadServices(requestType, controller);
        if (isMounted && result) {
          dispatch(actionSetServices(result));
        }
      })();

      return () => {
        isMounted = false;
        controller.abort();
      };
    },
    // eslint-disable-next-line
    [
      requestType,
      appLayoutState.languageTrans,
      appUserState.userData.countryCode,
    ]
  );

  useEffect(() => {
    if (isServicesLoading) return;
    setServicesCount(
      services.filter(
        (service) => service.service_status === ServiceStatus.AVAILABLE
      ).length
    );
    setSelectedServices([]);
    setSelectedServicesObject([]);
  }, [services, isServicesLoading]);

  if (cachedRequest) {
    UtilsRequest.removeRequestInfo();
  }

  // useEffect for closing confirmation modal if service request throws an error
  useEffect(() => {
    if (appMyDataState.serviceRequestToastError.isError) {
      setConfirmOpen(false);
      setSelectedServices([]);
      setSelectedServicesObject([]);
    }
  }, [confirmOpen, appMyDataState.serviceRequestToastError.isError]);

  useEffect(() => {
    if (requestNumber && requestNumber !== "" && !isRequestPending) {
      setSelectedServices([]);
      setSelectedServicesObject([]);
      history.push(`${ROUTES.MYDATA_HISTORY}/${requestNumber}`);
    }
  }, [requestNumber, isRequestPending]);

  const handleCheckboxOnChange = (service: ServiceEntity) => {
    if (selectedServices.includes(service.service_code)) {
      setSelectedServices(
        selectedServices.filter((s) => s !== service.service_code)
      );
      setSelectedServicesObject(
        selectedServicesObject.filter(
          (s) => s.service_code !== service.service_code
        )
      );
    } else {
      setSelectedServices([...selectedServices, service.service_code]);
      setSelectedServicesObject([...selectedServicesObject, service]);
    }
  };

  const handleSelectAllOnChange = () => {
    const filteredServices = services.filter(
      (service) => service.service_status === ServiceStatus.AVAILABLE
    );

    if (selectedServices.length === filteredServices.length) {
      setSelectedServices([]);
      setSelectedServicesObject([]);
    } else {
      setSelectedServices(
        filteredServices.map((service) => service.service_code)
      );
      setSelectedServicesObject(filteredServices);
    }
  };

  const handleClickServiceLink = (
    e: any,
    service: ServiceEntity,
    jd: number
  ) => {
    e.preventDefault();
    setSelectedService(service);

    if (jd > 0) {
      setSelectedServiceJD(UtilsDateTime.convertJoinedDate(jd));
    }
  };

  const myDataOnHandleClose = () => {
    // focus the last selected element when mydata toast is being close
    const lastElement = selectedServices.at(-1);

    const lastSelectedService = document.querySelector(
      "input#" + lastElement
    ) as HTMLElement;
    lastSelectedService.focus();

    setTimeout(() => {
      const lastSelectedService = document.querySelector(
        "input#" + selectedServices.at(-1)
      ) as HTMLElement;
      if (lastSelectedService) {
        lastSelectedService.focus();
      }
    }, 0);
    setSelectedServicesObject([]);
    setSelectedServices([]);
  };

  return (
    <div className="manage-apps" data-testid="manage-apps">
      {confirmOpen && (
        <FocusTrapping
          isActive={confirmOpen}
          onHandleClose={() => setConfirmOpen(false)}
          modalId="confirmation-modal"
        >
          <ConfirmationModal
            isOpen={confirmOpen}
            setOpen={(val: boolean) => setConfirmOpen(val)}
            variant={requestType}
            serviceList={selectedServicesObject}
          />
        </FocusTrapping>
      )}
      <Container>
        <div className="manage-apps__title-section">
          <Typography
            variant="h3"
            className="manage-apps__title-section__title"
          >
            {t("PRIVACY_MYDATA_MANAGE_TITLE")}
            <TTSButton value={t("PRIVACY_MYDATA_MANAGE_TITLE")} />
          </Typography>
          <div className="manage-apps__title-section__selection">
            <Tabs className="manage-apps__tabs">
              <Tabs.Tab
                buttonTestid="download-tab"
                type="2depth"
                name="manage_tab"
                title={t("PRIVACY_MYREQUEST_TAB_DOWNLOAD")}
                className={
                  requestType !== REQUEST_TYPE.DOWNLOAD && isServicesLoading
                    ? "disabled"
                    : "pointerCursor"
                }
                checked={requestType === REQUEST_TYPE.DOWNLOAD}
                onChange={() => setRequestType(REQUEST_TYPE.DOWNLOAD)}
                disabled={
                  requestType !== REQUEST_TYPE.DOWNLOAD && isServicesLoading
                }
              />
              <Tabs.Tab
                buttonTestid="delete-tab"
                type="2depth"
                name="manage_tab"
                className={
                  requestType !== REQUEST_TYPE.DELETE && isServicesLoading
                    ? "disabled"
                    : "pointerCursor"
                }
                title={t("PRIVACY_MYREQUEST_TAB_DELETION")}
                checked={requestType === REQUEST_TYPE.DELETE}
                onChange={() => setRequestType(REQUEST_TYPE.DELETE)}
                disabled={
                  requestType !== REQUEST_TYPE.DELETE && isServicesLoading
                }
              />
              <Tabs.Tab
                buttonTestid="unsubscribe-tab"
                type="2depth"
                name="manage_tab"
                className={
                  requestType !== REQUEST_TYPE.UNSUBSCRIBE && isServicesLoading
                    ? "disabled"
                    : "pointerCursor"
                }
                title={t("PRIVACY_MYREQUEST_TAB_OPTOUT")}
                checked={requestType === REQUEST_TYPE.UNSUBSCRIBE}
                onChange={() => setRequestType(REQUEST_TYPE.UNSUBSCRIBE)}
                disabled={
                  requestType !== REQUEST_TYPE.UNSUBSCRIBE && isServicesLoading
                }
              />
            </Tabs>
          </div>
        </div>
        {/* Loading Spinner */}
        {isServicesLoading ? (
          <Lottie
            className="manage-apps__loading"
            animationData={Loading}
            loop={true}
            style={{ width: 60, height: 60 }}
          />
        ) : (
          // Optional rendering for displaying error section or displaying services section
          <>
            {servicesHasErrors ? (
              <ServiceErrorDisplay />
            ) : (
              <ServicesListSection
                servicesCount={servicesCount}
                requestType={requestType}
                selectedServices={selectedServices}
                services={services}
                selectedServicesObject={selectedServicesObject}
                handleSelectAllOnChange={handleSelectAllOnChange}
                handleCheckboxOnChange={handleCheckboxOnChange}
                setIsServiceInfoOpen={setIsServiceInfoOpen}
                handleClickServiceLink={handleClickServiceLink}
                setConfirmOpen={setConfirmOpen}
                myDataOnHandleClose={myDataOnHandleClose}
              />
            )}
          </>
        )}
      </Container>

      <FocusTrapping
        onHandleClose={() => {
          if (selectedService) {
            const elementId =
              selectedService?.service_name + selectedService?.service_code;

            setTimeout(() => {
              const selectedServiceName = document.getElementById(elementId);
              if (selectedServiceName) {
                selectedServiceName.focus();
              }
            }, 0);
          }

          setSelectedService(undefined);
          setSelectedServiceJD("");
          setIsServiceInfoOpen(false);
        }}
        isActive={isServiceInfoOpen}
        modalId="service-info-modal"
      >
        <ServiceInfoModal
          service={selectedService}
          joinedDate={selectedServiceJD}
          requestType={requestType}
          onHide={() => {
            if (selectedService) {
              const selectedServiceName = document.getElementById(
                selectedService?.service_name + selectedService?.service_code
              );
              if (selectedServiceName) {
                selectedServiceName.focus();
              }
            }
            setSelectedService(undefined);
            setSelectedServiceJD("");
            setIsServiceInfoOpen(false);
          }}
          isOpen={isServiceInfoOpen}
        />
      </FocusTrapping>
    </div>
  );
};

interface ServiceInfoModalProps {
  service: ServiceEntity | undefined;
  joinedDate: string;
  requestType: string;
  onHide: () => void;
  isOpen: boolean;
}

const ServiceInfoModal: React.FC<ServiceInfoModalProps> = (props) => {
  const { service, isOpen } = props;
  const { t } = useTranslation();

  const getExtraInfo = () => {
    let description: string = "";

    switch (props.requestType) {
      case REQUEST_TYPE.DOWNLOAD:
        description = service?.service_description_download || "";
        break;
      case REQUEST_TYPE.DELETE:
        description = service?.service_description_delete || "";
        break;
      case REQUEST_TYPE.UNSUBSCRIBE:
        description = service?.service_description_optout || "";
        break;
      default:
        description = "";
        break;
    }

    return (
      description !== "" && (
        <>
          <br />
          <br />

          <Trans
            components={{
              a: (
                // eslint-disable-next-line
                <a target="_blank" />
              ),
            }}
            values={{
              "%s": service?.service_name,
            }}
          >
            {description}
          </Trans>
        </>
      )
    );
  };

  useEffect(() => {
    if (isOpen) {
      UtilsKeyboardAccessibility.setFocus("service-info-modal", {
        preventScroll: true,
      });
    }
  }, [isOpen]);

  useEffect(() => {
    UtilsKeyboardAccessibility.addTabEventListener(
      "service-info-tts",
      "pseudo-service-info"
    );
    UtilsKeyboardAccessibility.addTabEventListener(
      "close-service-info-modal",
      "service-info-modal"
    );

    return () => {
      UtilsKeyboardAccessibility.removeTabEventListener(
        "service-info-tts",
        "pseudo-service-info"
      );
      UtilsKeyboardAccessibility.removeTabEventListener(
        "close-service-info-modal",
        "service-info-modal"
      );
    };
  }, []);

  return (
    <Modal
      show={isOpen}
      wrapperClassName="service-info-modal"
      id="service-info-modal"
      autoHeight
    >
      <Modal.Header
        headertestid="onhide-service-info-modal"
        onHide={() => props.onHide()}
      >
        {service && (
          <>
            <Image
              image={service.service_icon_url}
              width={32}
              height={32}
              className="service-info-modal__icon"
            />
            <Modal.Title ttsButtonId="service-info-tts">
              {service.service_name}
            </Modal.Title>
          </>
        )}
      </Modal.Header>
      <Modal.Body>
        <div className="service-info-modal__description subText">
          {props.joinedDate !== "" && (
            <Typography
              variant="body2"
              className="service-info-modal__description__join-date"
            >
              Joined{" "}
              <Typography variant="body2" component="span" weight="wBold">
                {props.joinedDate}
              </Typography>
            </Typography>
          )}
          {service && (
            <Typography variant="body2">
              <Trans
                values={{
                  "%s": service.service_name,
                }}
              >
                {service.service_description}
              </Trans>
            </Typography>
          )}

          {getExtraInfo()}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          testId="onhide-service-info-modal-button"
          variant="solid"
          type="primary"
          title={t("PRIVACY_CANCEL_REQUEST_BTN_CLOSE")}
          className="btn-close"
          onClick={() => props.onHide()}
          id="close-service-info-modal"
        />
        <div id="pseudo-service-info"></div>
      </Modal.Footer>
    </Modal>
  );
};
